import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Badge, Checkbox, Empty, Input, Tooltip, Upload } from 'antd';
import { useSelector } from 'react-redux';
import {
  Button,
  Modal,
  SkeletonLoader,
  toast,
} from '@insight-health-ai/roseate/components';
import {
  FileAttachment02,
  Trash01,
  AlertTriangle,
  Edit01,
  Download01,
  FileDownload02,
} from '@insight-health-ai/roseate/icons';
import { LoadingOutlined } from '@ant-design/icons';
import {
  useGetEncounterDetails,
  useGetEncounterDocumentList,
  useLinkEncounterDocuments,
  useUnlinkEncounterDocuments,
} from 'src/hooks/encounter';
import { PatientDocument } from 'src/models';
import { analytics } from 'src/services';
import { useAuth } from '@frontegg/react';
import { NonOptional, useScreenSize } from '@insight-health-ai/roseate/utils';
import {
  useCreatePatientDocument,
  useUpdatePatientDocument,
  UpdatePatientDocumentPayload,
} from 'src/hooks/patient';
import { EditableField, PatientFileDocumentProps } from 'src/components';
import {
  useLinkAppointmentDocuments,
  useUnlinkAppointmentDocuments,
} from 'src/hooks/appointment';
import styled from '@emotion/styled';
import classNames from 'classnames';
import PDFRenderer from 'src/components/pdfRenderer';
import dayjs from 'dayjs';
import { downloadFile } from 'src/utils';
import { debounce } from 'lodash-es';

type Attachment = {
  isError?: boolean;
  isLoading?: boolean;
  document: NonOptional<
    Partial<PatientDocument>,
    'patient_document_id' | 'name' | 'type' | 'mime_type' | 'size'
  >;
};

const getDocumentFallbackName = ({
  date,
  prefix,
}: { date?: string; prefix?: string } = {}) => {
  return `${prefix || 'Notes'}: ${(date ? dayjs(date) : dayjs()).format('MMM DD, YYYY HH:mm')}`;
};

const minViewportVisibility = 64;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    height: calc(100vh - ${minViewportVisibility}px);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .ant-modal-body {
    height: 100%;
    overflow: hidden;
  }
`;

const StyledUpload = styled(Upload)`
  .ant-upload {
    width: 100%;
    display: block;
  }
`;

export type PreChartDocumentsProps = {
  isCompact?: boolean;
  source: PatientFileDocumentProps['source'];
  encounterId: string;
  getTrigger?: (currentState: {
    attachments: Attachment[];
    isLoading: boolean;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
  }) => React.ReactNode;
  onDocumentsChange?: () => void;
  onChange?: (documents: { documents: PatientDocument[] }) => void;
  onOpenChange?: (isOpen: boolean) => void;
};

const tempDocumentIdPrefix = '__temp__';
const maxAllowedUploadSize = 400000;
const bytesPerCharacter = 4;

export const PreChartDocuments = ({
  isCompact,
  encounterId,
  source,
  getTrigger,
  onChange,
  onDocumentsChange,
  onOpenChange,
}: PreChartDocumentsProps) => {
  const { mediaMax } = useScreenSize();
  const timeQuota = useSelector((state: any) => state?.quotaState?.timeQuota);
  const isTimeQuotaExpired =
    timeQuota?.remaining_days !== null && timeQuota?.remaining_days <= 0;
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);
  const [attachments, setAttachments] = useState<
    {
      isSelected?: boolean;
      isError?: boolean;
      isLoading?: boolean;
      document: NonOptional<
        Partial<PatientDocument>,
        'patient_document_id' | 'name' | 'type' | 'mime_type' | 'size'
      >;
    }[]
  >([]);

  const { data: encounterDetails } = useGetEncounterDetails({
    encounterId,
  });
  const {
    data: documentsData,
    isLoading: isLoadingDocuments,
    isFetching: isFetchingDocuments,
  } = useGetEncounterDocumentList(
    { encounterId, includePresignedUrl: true },
    { refetchOnWindowFocus: !isOpen }
  );
  const { mutateAsync: createPatientDocument } = useCreatePatientDocument();

  const { mutateAsync: unlinkEncounterDocuments } =
    useUnlinkEncounterDocuments();
  const { mutateAsync: unlinkAppointmentDocuments } =
    useUnlinkAppointmentDocuments();
  const { mutateAsync: linkDocumentToEncounter } = useLinkEncounterDocuments();
  const { mutateAsync: linkDocumentToAppointment } =
    useLinkAppointmentDocuments();
  const { mutateAsync: updatePatientDocument } = useUpdatePatientDocument();

  const { user } = useAuth();

  const selectedCount = attachments.filter(
    (attachment) => attachment.isSelected
  ).length;
  const currentAttachment = attachments[previewIndex];

  const currentTotalUploadedSize = useMemo(() => {
    return attachments.reduce((acc, { document }) => {
      return (
        acc +
        (document.type === 'text'
          ? (document.content?.length || 0) * bytesPerCharacter
          : document.size || 0)
      );
    }, 0);
  }, [attachments]);

  const remainingAllowedNotesLength = Math.floor(
    (maxAllowedUploadSize - currentTotalUploadedSize) / bytesPerCharacter
  );

  const validateFile = async (file: File) => {
    const fileObj = file;

    const newTotalHistorySize = currentTotalUploadedSize + (fileObj.size! || 0);
    if (newTotalHistorySize > maxAllowedUploadSize) {
      analytics.track(
        `Upload limit exceeded In ${
          source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
        } History Modal`,
        {
          totalUploadedFileSize: newTotalHistorySize,
          email: user?.email,
        }
      );
      Modal.warning({
        title: 'Upload limit exceeded',
        content: `The total size of all files uploaded should not exceed ${
          maxAllowedUploadSize / 1000
        }KB.`,
      });
      return false;
    }

    // check if file is already in attachments
    const similarFileExists = attachments.some(
      (attachment) =>
        attachment.document.name === fileObj.name &&
        attachment.document.mime_type === fileObj.type &&
        attachment.document.size === fileObj.size
    );
    if (similarFileExists) {
      return await Modal.confirm({
        centered: true,
        title: 'Similar file already attached',
        content: 'Do you still want to upload?',
        primaryAction: {
          label: 'Proceed',
          onClick: () => {
            analytics.track(
              `Clicked Proceed On Duplicate File Alert In ${
                source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
              } History Modal`,
              {
                email: user?.email,
              }
            );
          },
        },
        onClose: () => {
          analytics.track(
            `Clicked Cancel On Duplicate File Alert In ${
              source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
            } History Modal`,
            {
              email: user?.email,
            }
          );
        },
      });
    }

    return true;
  };

  const createNewDocument = async (fileData: File | string) => {
    const tempDocumentId = `${tempDocumentIdPrefix}${Date.now()}`;
    const notesFileName = getDocumentFallbackName();
    setAttachments([
      ...attachments,
      {
        isLoading: true,
        document: {
          patient_document_id: tempDocumentId,
          name: typeof fileData === 'string' ? notesFileName : fileData.name,
          type: typeof fileData === 'string' ? 'text' : 'file',
          mime_type:
            typeof fileData === 'string' ? 'text/plain' : fileData.type!,
          size: typeof fileData === 'string' ? fileData.length : fileData.size!,
        },
      },
    ]);

    try {
      const newDoc = await createPatientDocument(
        typeof fileData === 'string'
          ? {
              content: fileData,
              type: 'text',
              source,
            }
          : {
              type: 'file',
              file: fileData,
              size: fileData.size,
              source,
            }
      );
      if (source === 'pre_chart_history') {
        await linkDocumentToEncounter({
          encounterId,
          documentIds: [newDoc.patient_document_id],
        });
      } else if (source === 'vca_patient_history') {
        await linkDocumentToAppointment({
          appointmentId: encounterDetails?.appointment_id!,
          documentIds: [newDoc.patient_document_id],
        });
      }
      setAttachments((prev) =>
        prev.map((attachment) =>
          attachment.document.patient_document_id === tempDocumentId
            ? { document: newDoc }
            : attachment
        )
      );
      onDocumentsChange?.();
    } catch (error) {
      toast.error('Failed to upload file');
      setAttachments((prev) =>
        prev.map((attachment) =>
          attachment.document.patient_document_id === tempDocumentId
            ? { ...attachment, isLoading: false, hasError: true }
            : attachment
        )
      );
    }
  };

  const remainingAllowedNotesLengthRef = useRef(remainingAllowedNotesLength);
  remainingAllowedNotesLengthRef.current = remainingAllowedNotesLength;
  const updateDocument = useCallback(
    async ({ patientDocumentId, ...payload }: UpdatePatientDocumentPayload) => {
      if (remainingAllowedNotesLengthRef.current < 0) {
        return;
      }

      try {
        await updatePatientDocument({
          patientDocumentId,
          ...payload,
        });
        onDocumentsChange?.();
      } catch (error) {
        toast.error('Failed to update document');
        throw error;
      }
    },
    [updatePatientDocument]
  );

  const updateDocumentDebounced = useMemo(
    () => debounce(updateDocument, 500),
    [updateDocument]
  );

  const deleteDocuments = async (patientDocumentIds: string[]) => {
    if (!patientDocumentIds.every((id) => id.includes(tempDocumentIdPrefix))) {
      const confirmed = await Modal.confirm({
        appearance: 'error',
        title: 'Delete patient documents?',
        content: `Are you sure you want to delete ${patientDocumentIds.length} document(s)?`,
        primaryAction: {
          label: 'Delete',
          onClick: async () => {
            try {
              if (source === 'pre_chart_history') {
                await unlinkEncounterDocuments({
                  encounterId,
                  documentIds: patientDocumentIds,
                });
              } else if (source === 'vca_patient_history') {
                await unlinkAppointmentDocuments({
                  appointmentId: encounterDetails?.appointment_id!,
                  documentIds: patientDocumentIds,
                });
              }
            } catch (error) {
              toast.error('Failed to delete document');
              throw error;
            }
          },
        },
      });
      if (!confirmed) {
        return;
      }
    }
    setAttachments(
      attachments.filter(
        ({ document }) =>
          !patientDocumentIds.includes(document.patient_document_id)
      )
    );
    onDocumentsChange?.();
  };

  useEffect(() => {
    setAttachments(
      (documentsData?.encounterDocuments || [])?.map((attachment) => ({
        document: attachment,
      }))
    );
  }, [!!documentsData?.encounterDocuments]);

  useEffect(() => {
    onOpenChange?.(!!isOpen);
  }, [!!isOpen]);

  useEffect(() => {
    const currentAttachmentId = currentAttachment?.document.patient_document_id;
    const index = attachments.findIndex(
      (attachment) =>
        attachment.document.patient_document_id === currentAttachmentId
    );
    if (index > -1) {
      setPreviewIndex(index);
    } else {
      setPreviewIndex(0);
    }
  }, [attachments.length]);

  useEffect(() => {
    onChange?.({
      documents: attachments.map(
        ({ document }) => document
      ) as PatientDocument[],
    });
  }, [attachments]);

  const renderAttachment = (attachment: (typeof attachments)[number]) => {
    if (!attachment) {
      return null;
    }

    const { document, isLoading, isError } = attachment;

    if (isLoading) {
      return (
        <div className="h-full w-full p-4">
          <SkeletonLoader active />
        </div>
      );
    }

    if (isError) {
      return (
        <Empty
          image={<AlertTriangle className="text-red-500 text-[80px]" />}
          description="An error occurred while loading the document"
        />
      );
    }

    if (document.type === 'text') {
      return (
        <Input.TextArea
          key={document.patient_document_id}
          className="rounded-t-lg rounded-b-none border-transparent focus:border-solid"
          placeholder="Paste or type your notes here"
          disabled={isLoadingDocuments || isFetchingDocuments}
          autoFocus
          style={{ resize: 'none', height: '100%' }}
          defaultValue={document.content || ''}
          onChange={(e) => {
            const content = e.target.value;
            setAttachments((prev) =>
              prev.map((attachment) =>
                attachment.document.patient_document_id ===
                document.patient_document_id
                  ? {
                      ...attachment,
                      document: { ...attachment.document, content },
                    }
                  : attachment
              )
            );
            updateDocumentDebounced({
              patientDocumentId: document.patient_document_id,
              content: e.target.value,
            });
          }}
          onBlur={(e) => {
            const content = e.target.value;
            updateDocument({
              patientDocumentId: document.patient_document_id,
              content,
            });
          }}
        />
      );
    }

    if (document.mime_type === 'application/pdf') {
      return <PDFRenderer url={document.presigned_url ?? ''} />;
    }
    if (document.mime_type?.includes('image')) {
      return (
        <div className="w-full h-full overflow-hidden flex items-center justify-center">
          <img
            className="w-full h-full object-contain"
            src={document.presigned_url ?? ''}
            alt={document.name ?? ''}
          />
        </div>
      );
    }
    return (
      <div className="w-full h-full overflow-hidden flex items-center justify-center p-4">
        <Empty
          image={<FileDownload02 className="text-[80px]" />}
          description="The document type is not supported for preview. Please download to open it."
        />
      </div>
    );
  };

  const renderExtras = (attachment: (typeof attachments)[number]) => {
    const { document } = attachment;

    return (
      <div className="flex gap-2 justify-between w-full">
        <div className="flex gap-2 flex-shrink-0">
          <Button
            compact={mediaMax('sm')}
            appearance="danger"
            variant="outlined"
            icon={<Trash01 />}
            label="Delete"
            onClick={() => deleteDocuments([document.patient_document_id])}
          />
          {!!document.presigned_url && (
            <Button
              compact={mediaMax('sm')}
              appearance="secondary"
              variant="outlined"
              icon={<Download01 />}
              label="Download"
              onClick={() =>
                downloadFile(document.presigned_url!, document.name!)
              }
            />
          )}
        </div>
        {document.type === 'file' && (
          <div className="flex-1 w-full flex items-center overflow-hidden text-ellipsis whitespace-nowrap">
            <EditableField
              text={document.description || ''}
              className="w-full text-right"
              size="md"
              onSave={async (value: string) => {
                await updateDocument({
                  patientDocumentId: document.patient_document_id,
                  description: value,
                });
                setAttachments((prev) =>
                  prev.map((attachment) =>
                    attachment.document.patient_document_id ===
                    document.patient_document_id
                      ? {
                          ...attachment,
                          document: {
                            ...attachment.document,
                            description: value,
                          },
                        }
                      : attachment
                  )
                );
              }}
            >
              <div className="w-full overflow-hidden text-ellipsis whitespace-nowrap">
                {document.description ? (
                  document.description
                ) : (
                  <div className="text-gray-500">Add additional details</div>
                )}
              </div>
            </EditableField>
          </div>
        )}
        {document.type === 'text' && (
          <div
            className={`text-sm text-gray-500 flex items-center w-full justify-end flex-wrap gap-2 ${
              remainingAllowedNotesLength < 0 ? 'text-red-500' : ''
            }`}
          >
            {remainingAllowedNotesLength >= 0 && (
              <span className="text-right">
                {remainingAllowedNotesLength}
                {' characters remaining'}
              </span>
            )}
            {remainingAllowedNotesLength < 0 && (
              <span>
                You have exceeded the maximum allowed character limit. Changes
                will not be saved.
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTrigger = () => {
    if (getTrigger) {
      return getTrigger({
        attachments,
        isLoading: isLoadingDocuments,
        isOpen,
        setIsOpen,
      });
    }

    if (attachments.length > 0) {
      return (
        <Badge className="z-10" count={attachments.length} offset={[-4, 10]}>
          <Button
            variant="outlined"
            icon={
              isLoadingDocuments ? <LoadingOutlined /> : <FileAttachment02 />
            }
            onClick={() => setIsOpen(true)}
          >
            {isCompact ? null : 'Documents attached'}
          </Button>
        </Badge>
      );
    }

    return (
      <Tooltip title={isTimeQuotaExpired ? 'Your free trial has expired.' : ''}>
        <span className="inline-block">
          <Button
            variant="outlined"
            icon={
              isLoadingDocuments ? <LoadingOutlined /> : <FileAttachment02 />
            }
            onClick={() => setIsOpen(true)}
            disabled={isTimeQuotaExpired}
          >
            {isCompact ? null : 'Add documents'}
          </Button>
        </span>
      </Tooltip>
    );
  };

  const renderModalContent = () => {
    const addOptionsJsx = (
      <>
        <Button
          className={classNames({
            'flex-1': mediaMax('sm') && !!attachments.length,
          })}
          block
          appearance="primary"
          icon={<Edit01 />}
          onClick={() => createNewDocument('')}
        >
          Add notes
        </Button>
        <StyledUpload
          className={classNames({
            'flex-1': mediaMax('sm') && !!attachments.length,
          })}
          fileList={[]}
          name="files"
          accept=".doc,.docx,.txt,.pdf"
          beforeUpload={() => {
            return false;
          }}
          onChange={async ({ file }) => {
            if (await validateFile(file as unknown as File)) {
              await createNewDocument(file as unknown as File);
            }
          }}
        >
          <Button
            block
            appearance="primary"
            icon={<FileAttachment02 />}
            onClick={() => {
              analytics.track(
                `Clicked Upload Document In ${
                  source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                } History Modal`,
                {
                  email: user?.email,
                }
              );
            }}
          >
            Attach document
          </Button>
        </StyledUpload>
      </>
    );

    if (!attachments.length) {
      return (
        <div className="flex flex-col h-full w-full justify-center items-center bg-white rounded-lg overflow-hidden">
          <Empty description="No history attached">
            <div className="flex flex-col gap-4 w-[280px]">{addOptionsJsx}</div>
          </Empty>
        </div>
      );
    }

    return (
      <div className="flex flex-col md:flex-row gap-4 h-full w-full md:overflow-hidden">
        <div className="flex flex-col flex-shrink-0 md:h-full md:w-[240px] bg-white rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 p-2 border-b flex-shrink-0 shadow-sm justify-between min-h-[42px]">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Checkbox
                checked={attachments.every(
                  (attachment) => attachment.isSelected || false
                )}
                onChange={(e) => {
                  setAttachments((prev) =>
                    prev.map((a) => ({ ...a, isSelected: e.target.checked }))
                  );
                }}
              />
              {selectedCount
                ? `${selectedCount === attachments.length ? 'All' : selectedCount} selected`
                : 'Select all'}
            </div>
            <div>
              {!!selectedCount && (
                <Button
                  label="Delete Selected"
                  compact
                  appearance="danger"
                  size="sm"
                  icon={<Trash01 />}
                  onClick={() =>
                    deleteDocuments(
                      attachments
                        .filter((attachment) => attachment.isSelected)
                        .map(
                          (attachment) =>
                            attachment.document.patient_document_id
                        )
                    )
                  }
                />
              )}
            </div>
          </div>
          <div className="flex md:flex-col h-full overflow-y-auto">
            {attachments.map((attachment, index) => {
              const { isSelected, document, isLoading } = attachment;
              return (
                <div
                  key={document.patient_document_id}
                  className={classNames(
                    'p-2 flex gap-2 border-b-0 border-r md:border-r-0 md:border-b border-gray-100 cursor-pointer flex-1 md:flex-none min-w-[200px]',
                    {
                      'bg-green-100': isSelected,
                      'bg-gray-100': !isSelected && previewIndex === index,
                      'opacity-50': isLoading,
                    }
                  )}
                  onClick={() => setPreviewIndex(index)}
                >
                  <div className="flex-shrink-0">
                    {isLoading ? (
                      <LoadingOutlined />
                    ) : (
                      <Checkbox
                        checked={isSelected || false}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          setAttachments((prev) =>
                            prev.map((a) =>
                              a.document.patient_document_id ===
                              document.patient_document_id
                                ? {
                                    ...a,
                                    isSelected: e.target.checked,
                                  }
                                : a
                            )
                          );
                        }}
                      />
                    )}
                  </div>
                  <div className="flex flex-col overflow-hidden">
                    <div className="text-sm font-medium">
                      {document.name ||
                        getDocumentFallbackName({
                          date: document.last_modified || undefined,
                          prefix:
                            document.type === 'text' ? undefined : 'Document',
                        })}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-gray-500">
                        {document.type === 'text'
                          ? `${document.content?.length || 0} characters`
                          : `${((document.size || 0) / 1000).toFixed(2)} KB`}
                      </div>
                      {document.source === 'ehr' && (
                        <div className="text-xs text-purple-500">
                          EHR Synced
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex md:flex-col gap-2 p-2 border-t flex-shrink-0">
            {addOptionsJsx}
          </div>
        </div>
        <div className="flex-1 flex flex-col h-full bg-white rounded-lg overflow-hidden">
          <div className="w-full h-full overflow-y-auto flex justify-center items-center">
            {!!currentAttachment && renderAttachment(currentAttachment)}
          </div>
          <div className="flex-shrink-0 border-t p-2 flex justify-between">
            {!!currentAttachment && renderExtras(currentAttachment)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderTrigger()}
      <StyledModal
        title="Patient notes & documents"
        open={isOpen}
        size="2xl"
        onClose={() => {
          setIsOpen(false);
        }}
        footer={({ secondaryAction }) => {
          if (attachments.length === 0) {
            return null;
          }
          return (
            <div className="flex justify-end">
              <Button {...secondaryAction} />
            </div>
          );
        }}
        secondaryAction={{
          label: 'Save History',
        }}
      >
        {renderModalContent()}
      </StyledModal>
    </>
  );
};
